import React, { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { format } from "date-fns";
import { Copy, ExternalLink, Download, Trash2, Users, Check, X, HelpCircle, Eye, Pencil } from "lucide-react";
import PublishDialog from "@/components/PublishDialog";
import CoHostSection from "@/components/CoHostSection";
import PrintableInviteCard from "@/components/PrintableInviteCard";
import { useAuth } from "@/context/AuthContext";

export default function EventTracker() {
  const { user } = useAuth();
  const { id } = useParams();
  const nav = useNavigate();
  const [event, setEvent] = useState(null);
  const [rsvps, setRsvps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [publishOpen, setPublishOpen] = useState(false);
  const [downloadingInvite, setDownloadingInvite] = useState(false);
  const [themeArt, setThemeArt] = useState({ top: null, bottom: null });
  const printableRef = useRef(null);

  const load = async () => {
    try {
      const ev = await api.get(`/events/${id}`);
      setEvent(ev.data);
      const rs = await api.get(`/events/${id}/rsvps`);
      setRsvps(rs.data);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [id]);

  // Preload the illustrated theme art for print (cached on server; no LLM cost)
  useEffect(() => {
    if (!event || event.motif_density !== "busy") return;
    const params = new URLSearchParams({
      theme: event.theme || "",
      custom_theme: event.custom_theme || "",
      event_type: event.event_type || "",
    }).toString();
    api.get(`/theme-art?${params}`).then(({ data }) => {
      if (data?.top_path && data?.bottom_path) {
        setThemeArt({
          top: `${window.location.origin}/api/uploads/file/${data.top_path}`,
          bottom: `${window.location.origin}/api/uploads/file/${data.bottom_path}`,
        });
      }
    }).catch(() => {});
  }, [event]);

  if (loading) return <div className="min-h-screen bg-[#FBF9F4]"><Navbar /><div className="p-10 text-neutral-500">Loading…</div></div>;
  if (!event) return <div className="min-h-screen bg-[#FBF9F4]"><Navbar /><div className="p-10">Not found</div></div>;

  const isPublished = event.status === "published";
  const isOwner = user?.id === event.owner_id;
  const publicUrl = `${window.location.origin}/i/${event.slug}`;

  const copy = () => { navigator.clipboard.writeText(publicUrl); toast.success("Link copied"); };
  const downloadInvite = async () => {
    if (!event) return;
    setDownloadingInvite(true);
    try {
      const el = printableRef.current;
      if (!el) { toast.error("Preparing the invite — try again in a moment"); return; }
      // Give any images (theme art, banner) time to fully decode
      const imgs = el.querySelectorAll("img");
      await Promise.all(Array.from(imgs).map((img) => {
        if (img.complete && img.naturalWidth > 0) return Promise.resolve();
        return new Promise((res) => { img.onload = res; img.onerror = res; });
      }));
      const [{ default: html2canvas }, jsPDFMod] = await Promise.all([
        import("html2canvas"),
        import("jspdf"),
      ]);
      const jsPDF = jsPDFMod.jsPDF || jsPDFMod.default;
      const canvas = await html2canvas(el, {
        backgroundColor: null,
        scale: 2,
        useCORS: true,
        allowTaint: false,
        logging: false,
      });
      const pdf = new jsPDF({ unit: "mm", format: "a5", orientation: "portrait" });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const imgData = canvas.toDataURL("image/jpeg", 0.92);
      const canvasRatio = canvas.width / canvas.height;
      const pageRatio = pageW / pageH;
      let rw, rh, ox = 0, oy = 0;
      if (canvasRatio > pageRatio) { rw = pageW; rh = pageW / canvasRatio; oy = (pageH - rh) / 2; }
      else { rh = pageH; rw = pageH * canvasRatio; ox = (pageW - rw) / 2; }
      pdf.addImage(imgData, "JPEG", ox, oy, rw, rh);
      pdf.save(`${event.slug || "invite"}.pdf`);
      toast.success("Invite PDF saved");
    } catch (e) {
      console.error("PDF render failed:", e);
      toast.error("Could not render PDF — try again");
    } finally {
      setDownloadingInvite(false);
    }
  };
  const downloadQR = () => {
    const svg = document.getElementById("qr-svg");
    if (!svg) return;
    const s = new XMLSerializer().serializeToString(svg);
    const blob = new Blob([s], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `${event.slug}-qr.svg`; a.click();
    URL.revokeObjectURL(url);
  };
  const removeRsvp = async (rid) => {
    if (!window.confirm("Remove this RSVP?")) return;
    try { await api.delete(`/rsvps/${rid}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  const exportCsv = () => {
    const headers = ["Name", "Email", "Attending", "Guests", "Dietary", "Message", "Submitted"];
    const rows = rsvps.map((r) => [r.name, r.email, r.attending, r.guest_count, r.dietary, (r.message || "").replace(/\n/g, " "), r.created_at]);
    const csv = [headers, ...rows].map((row) => row.map((c) => `"${String(c || "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `${event.slug}-rsvps.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const stats = {
    total: rsvps.length,
    yes: rsvps.filter((r) => r.attending === "yes").length,
    no: rsvps.filter((r) => r.attending === "no").length,
    maybe: rsvps.filter((r) => r.attending === "maybe").length,
    guests: rsvps.filter((r) => r.attending === "yes").reduce((s, r) => s + (r.guest_count || 1), 0),
  };

  const filtered = filter === "all" ? rsvps : rsvps.filter((r) => r.attending === filter);

  let when = "";
  try { when = format(new Date(event.event_datetime), "EEEE, MMMM d · p"); } catch {}

  return (
    <div className="min-h-screen bg-[#FBF9F4]" style={{ fontFamily: "'Manrope', sans-serif" }}>
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
        <Link to="/dashboard" className="text-xs tracking-[0.25em] uppercase text-neutral-500 hover:text-black">← Back to dashboard</Link>
        <div className="mt-4 flex items-start justify-between flex-wrap gap-6">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500">{event.event_type.replace("_", " ")}{!isOwner && <span className="ml-3 text-neutral-400">· Co-host</span>}</div>
            <h1 style={{ fontFamily: "'Cormorant Garamond', serif" }} className="text-4xl sm:text-5xl mt-2 font-light tracking-tight">{event.title}</h1>
            <div className="mt-2 text-neutral-600">{when} · {event.location_name}</div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Link to={`/events/${event.id}/edit`}>
              <Button variant="outline" data-testid="tracker-edit" className="rounded-sm"><Pencil size={14} className="mr-2" /> Edit invite</Button>
            </Link>
            <Link to={`/preview/${event.id}`}>
              <Button variant="outline" data-testid="tracker-preview" className="rounded-sm"><Eye size={14} className="mr-2" /> Preview</Button>
            </Link>
            <Button
              onClick={downloadInvite}
              disabled={!isPublished || downloadingInvite}
              variant="outline"
              data-testid="tracker-download-invite"
              className="rounded-sm"
              title={isPublished ? "Download an image of your invite" : "Publish to enable download"}
            >
              <Download size={14} className="mr-2" /> {downloadingInvite ? "Rendering…" : "Download PDF"}
            </Button>
            <a href={publicUrl} target="_blank" rel="noreferrer">
              <Button variant="outline" data-testid="view-invite-btn" className="rounded-sm" disabled={!isPublished}><ExternalLink size={14} className="mr-2" /> View public invite</Button>
            </a>
          </div>
        </div>

        {!isPublished && (
          <div data-testid="draft-banner" className="mt-8 border border-amber-300 bg-amber-50 p-5 flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-amber-900 font-medium">Draft · Not shareable</div>
              <p className="mt-2 text-sm text-amber-950 max-w-xl">
                This invite is in preview mode. The shareable link and RSVP form are locked until you publish it.
                Publishing uses one credit.
              </p>
            </div>
            <div className="flex gap-2">
              <Link to={`/preview/${event.id}`}>
                <Button variant="outline" data-testid="tracker-preview" className="rounded-sm bg-white"><Eye size={14} className="mr-2" /> Preview</Button>
              </Link>
              <Button onClick={() => setPublishOpen(true)} data-testid="tracker-publish" className="rounded-sm bg-neutral-900 hover:bg-neutral-800">
                Publish
              </Button>
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-10">
          {[
            { k: "total", label: "Responses", v: stats.total, Icon: Users },
            { k: "yes", label: "Yes", v: stats.yes, Icon: Check },
            { k: "maybe", label: "Maybe", v: stats.maybe, Icon: HelpCircle },
            { k: "no", label: "No", v: stats.no, Icon: X },
            { k: "guests", label: "Guest count", v: stats.guests, Icon: Users },
          ].map(({ k, label, v, Icon }) => (
            <div key={k} className="border border-black/10 bg-white p-5" data-testid={`stat-${k}`}>
              <div className="flex items-center justify-between">
                <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500">{label}</div>
                <Icon size={14} className="text-neutral-400" />
              </div>
              <div style={{ fontFamily: "'Cormorant Garamond', serif" }} className="text-5xl mt-3 font-light tabular-nums">{v}</div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-12 gap-6 mt-10">
          {/* Share card */}
          <div className="col-span-12 lg:col-span-4 border border-black/10 bg-white p-7">
            <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500">Share your invite</div>
            <div className="mt-4 flex items-center justify-center bg-[#FBF9F4] p-6 border border-black/5">
              <QRCodeSVG id="qr-svg" value={publicUrl} size={170} bgColor="#FBF9F4" fgColor="#1A1A1A" />
            </div>
            <div className="mt-4 break-all text-xs text-neutral-600 border border-black/10 p-3 bg-[#FBF9F4]" data-testid="share-link">{publicUrl}</div>
            <div className="mt-3 flex gap-2">
              <Button onClick={copy} variant="outline" data-testid="copy-link" className="flex-1 rounded-sm"><Copy size={14} className="mr-2" /> Copy</Button>
              <Button onClick={downloadQR} variant="outline" data-testid="download-qr" className="flex-1 rounded-sm"><Download size={14} className="mr-2" /> QR</Button>
            </div>
          </div>

          {/* RSVP list */}
          <div className="col-span-12 lg:col-span-8 border border-black/10 bg-white">
            <div className="p-6 flex items-center justify-between flex-wrap gap-3 border-b border-black/5">
              <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500">Guest list</div>
              <div className="flex gap-1">
                {["all", "yes", "maybe", "no"].map((f) => (
                  <button
                    key={f}
                    onClick={() => setFilter(f)}
                    data-testid={`filter-${f}`}
                    className={`px-3 py-1 text-xs tracking-[0.2em] uppercase ${filter === f ? "bg-neutral-900 text-white" : "bg-white text-neutral-600 hover:bg-neutral-100"}`}
                  >
                    {f}
                  </button>
                ))}
                <Button onClick={exportCsv} variant="ghost" data-testid="export-csv" className="rounded-sm ml-2 text-xs"><Download size={14} className="mr-1" /> CSV</Button>
              </div>
            </div>
            {filtered.length === 0 ? (
              <div className="p-12 text-center text-neutral-500">No responses yet. Share your invite to get started.</div>
            ) : (
              <div className="divide-y divide-black/5">
                {filtered.map((r) => (
                  <div key={r.id} data-testid={`rsvp-row-${r.id}`} className="p-5 flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="font-medium">{r.name}</span>
                        <span className={`text-[10px] tracking-[0.2em] uppercase px-2 py-0.5 ${r.attending === "yes" ? "bg-emerald-100 text-emerald-900" : r.attending === "maybe" ? "bg-amber-100 text-amber-900" : "bg-neutral-200 text-neutral-700"}`}>
                          {r.attending}
                        </span>
                        {r.attending === "yes" && <span className="text-xs text-neutral-500">+ {r.guest_count} guest{r.guest_count > 1 ? "s" : ""}</span>}
                        {r.referred_by_name && (
                          <span data-testid={`referred-by-${r.id}`} className="text-[10px] tracking-[0.15em] uppercase px-2 py-0.5 bg-neutral-900 text-white">
                            via {r.referred_by_name}
                          </span>
                        )}
                        {r.referral_count > 0 && (
                          <span data-testid={`brought-count-${r.id}`} className="text-[10px] tracking-[0.15em] uppercase px-2 py-0.5 bg-emerald-900 text-white">
                            brought {r.referral_count}
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-neutral-600 mt-1">{r.email}</div>
                      {r.dietary && <div className="text-xs text-neutral-500 mt-1">Dietary: {r.dietary}</div>}
                      {r.message && <div className="text-sm text-neutral-700 mt-2 italic">&ldquo;{r.message}&rdquo;</div>}
                      {r.answers && Object.keys(r.answers).length > 0 && (
                        <div className="mt-2 text-xs text-neutral-500 space-y-1">
                          {(event.custom_questions || []).map((q) => {
                            const v = r.answers[q.id];
                            if (v == null || v === "" || (Array.isArray(v) && v.length === 0)) return null;
                            return <div key={q.id}><span className="text-neutral-400">{q.label}:</span> {Array.isArray(v) ? v.join(", ") : String(v)}</div>;
                          })}
                        </div>
                      )}
                    </div>
                    <button onClick={() => removeRsvp(r.id)} data-testid={`delete-rsvp-${r.id}`} className="text-neutral-400 hover:text-red-600">
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Co-hosts */}
      <div className="max-w-7xl mx-auto px-6 lg:px-10 pb-12 -mt-4">
        <CoHostSection event={event} onChanged={load} />
      </div>

      <PublishDialog open={publishOpen} onOpenChange={setPublishOpen} event={event} onPublished={load} />

      {/* Hidden printable card — mounted so the ref is ready as soon as user clicks Download */}
      <div style={{ position: "fixed", left: -10000, top: 0, pointerEvents: "none" }} aria-hidden="true">
        <PrintableInviteCard
          ref={printableRef}
          event={{ ...event, theme_art_top_url: themeArt.top, theme_art_bottom_url: themeArt.bottom }}
          publicUrl={publicUrl}
        />
      </div>
    </div>
  );
}
